import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, input } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AgGridAngular } from 'ag-grid-angular';
import type { ColDef } from 'ag-grid-community';
import { shsThemeDark } from '@shs-digi-care-monorepo/frontend-lib';
import { IIroNamMaterialItem } from '@shs-digi-care-monorepo/interfaces';
import { productLineLabelKey } from '../../../../shared/models/temp.model';

@Component({
    selector: 'app-deal-information-detail',
    templateUrl: './deal-information.component.html',
    imports: [TranslateModule, AgGridAngular],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class DealInformationDetail {
    public isInstrumentOnly = input<boolean>(false);
    public productLine = input<string | null>(null);
    public soldTo = input<string | null>(null);
    public shipTo = input<string | null>(null);
    public soldToName = input<string | null>(null);
    public soldToAddress = input<string | null>(null);
    public shipToAddress = input<string | null>(null);
    public materials = input<IIroNamMaterialItem[]>([]);

    private translate = inject(TranslateService);

    protected dealTypeLabel = computed(() =>
        this.isInstrumentOnly()
            ? this.translate.instant('iro.deal-type.instrument-only')
            : this.translate.instant('iro.deal-type.standard-deal')
    );

    protected productLineLabel = computed(() => {
        const value = this.productLine();
        return value ? this.translate.instant(productLineLabelKey(value)) : '';
    });

    protected instrumentOnlyColDefs: ColDef[] = [
        { field: 'productLine', headerName: this.translate.instant('iro.grid.product-line') },
        { field: 'description', headerName: this.translate.instant('iro.grid.description') },
        { field: 'smn', headerName: this.translate.instant('iro.grid.smn') },
        { field: 'kitSizeDescription', headerName: this.translate.instant('iro.grid.kit-size-description') },
        { field: 'kitSize', headerName: this.translate.instant('iro.grid.kit-size') },
        { field: 'annualTestVolume', headerName: this.translate.instant('iro.grid.annual-test-volume') },
        { field: 'inventoryAmt', headerName: this.translate.instant('iro.grid.inventory-amount') },
        { field: 'implementationQuantity', headerName: this.translate.instant('iro.grid.implementation-quantity') },
        { field: 'orderQuantity', headerName: this.translate.instant('iro.grid.order-quantity') },
        { field: 'unitPrice', headerName: this.translate.instant('iro.grid.unit-price') },
        { field: 'totalPrice', headerName: this.translate.instant('iro.grid.total-price') }
    ];

    protected standardDealColDefs: ColDef[] = [
        { field: 'productLine', headerName: this.translate.instant('iro.grid.product-line') },
        { field: 'description', headerName: this.translate.instant('iro.grid.description') },
        { field: 'smn', headerName: this.translate.instant('iro.grid.smn') },
        { field: 'kitSizeDescription', headerName: this.translate.instant('iro.grid.kit-size-description') },
        { field: 'kitSize', headerName: this.translate.instant('iro.grid.kit-size') },
        { field: 'annualTestVolume', headerName: this.translate.instant('iro.grid.annual-test-volume') }
    ];

    protected colDefs = computed<ColDef[]>(() =>
        this.isInstrumentOnly() ? this.instrumentOnlyColDefs : this.standardDealColDefs
    );

    protected gridOptions = {
        theme: shsThemeDark
    };
}
